"use client";

import { useCallback, useEffect, useRef, useState } from "react";

interface QueryOptions {
  enabled?: boolean;
  cacheTime?: number;
}

interface CacheItem<T> {
  data: T;
  timestamp: number;
}

const cache: Record<string, CacheItem<unknown>> = {};

export const useQuery = <T>(
  queryKey: (number | string)[] | string | number,
  queryFn: () => Promise<T>,
  options: QueryOptions = { enabled: true },
) => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const key = Array.isArray(queryKey)
    ? queryKey.join("-")
    : queryKey.toString();
  const isFetching = useRef(false);

  const fetchData = useCallback(
    async (isRefetch?: boolean) => {
      if (isFetching.current || (error && !options.enabled)) return;
      isFetching.current = true;

      setLoading(true);
      setError(null);
      try {
        const cachedData = cache[key] as CacheItem<T> | undefined;
        const now = Date.now();
        if (
          !isRefetch &&
          cachedData &&
          (!options.cacheTime || now - cachedData.timestamp < options.cacheTime)
        ) {
          setData(cachedData.data);
          setLoading(false);
          isFetching.current = false;
          delete cache[key];
          return;
        }

        const result = await queryFn();
        cache[key] = { data: result, timestamp: now };
        setData(result);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "An error occurred";
        setError(new Error(errorMessage));
      } finally {
        setLoading(false);
        isFetching.current = false;
      }
    },
    [key, queryFn, options.cacheTime, error, options.enabled],
  );
  useEffect(() => {
    if (options.enabled && !error) {
      fetchData();
    }
  }, [fetchData, options.enabled, loading, error]);

  const refetch = useCallback(async () => {
    setError(null);
    setLoading(false);
    await fetchData(true);
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch,
    isError: !!error,
  };
};
